import { useState, type FormEvent } from 'react';
import type { JournalEntry, RecordPayment } from '@syndiqia/ledger/contracts';
import { ApiError, errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useFiscalYears, useLedgerMutation, useStatement } from '../../ledger-queries';
import { useCollectionsStatus } from '../../approvals-queries';
import { useSyndicate } from '../../session';
import { toCents, today } from './FinancesNav';

/** The unit's account with the syndicate: charges, payments, running balance; board can record a payment. */
export function UnitAccount({ unitId }: { unitId: string }) {
  const { t, money, date } = useI18n();
  const { can } = useSyndicate();
  const years = useFiscalYears();
  const hasLedger = !!years.data?.length;
  const statement = useStatement(unitId, hasLedger);
  const collections = useCollectionsStatus();
  const stage = collections.data?.find((c) => c.unitId === unitId);

  if (years.isPending) return <Loading label={t.loading} />;
  return (
    <section>
      <h2>{t.statement.title}</h2>
      {!hasLedger && <Empty>{t.statement.noLedger}</Empty>}
      {statement.isError && !(statement.error instanceof ApiError && statement.error.status === 409) && (
        <Alert>{errorMessage(statement.error, t)}</Alert>
      )}
      {statement.data && (
        <>
          <p>
            <strong>
              {t.statement.balance} : {money(statement.data.balanceCents)}
            </strong>
            {stage && stage.stage > 0 && (
              <span className="badge badge-draft">
                {' '}
                {t.collections.stage[stage.stage]}
                {stage.lastActionAt && ` — ${date(stage.lastActionAt)}`}
              </span>
            )}
            {statement.data.byFund.length > 1 && (
              <span className="muted">
                {' '}
                · {statement.data.byFund.map((f) => `${f.fundCode} ${money(f.balanceCents)}`).join(' · ')}
              </span>
            )}
          </p>
          {statement.data.lines.length === 0 ? (
            <Empty>{t.statement.empty}</Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t.statement.date}</th>
                  <th>{t.journal.description}</th>
                  <th className="num">{t.statement.charge}</th>
                  <th className="num">{t.statement.payment}</th>
                  <th className="num">{t.statement.balance}</th>
                </tr>
              </thead>
              <tbody>
                {[...statement.data.lines].reverse().map((l) => (
                  <tr key={l.entryId}>
                    <td className="nowrap">{date(l.date)}</td>
                    <td>{l.description}</td>
                    <td className="num">{l.chargeCents ? money(l.chargeCents) : ''}</td>
                    <td className="num">{l.paymentCents ? money(l.paymentCents) : ''}</td>
                    <td className="num">{money(l.balanceCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {can('ledger.payment.record') && (
            <Disclosure label={t.statement.recordPayment}>{(close) => <PaymentForm unitId={unitId} onDone={close} />}</Disclosure>
          )}
        </>
      )}
    </section>
  );
}

function PaymentForm({ unitId, onDone }: { unitId: string; onDone: () => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState({ date: today(), amount: '', reference: '' });
  const [error, setError] = useState<string | null>(null);
  const record = useLedgerMutation<RecordPayment, JournalEntry>((api, base, input) =>
    api(`${base}/payments`, { method: 'POST', body: input }),
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const amountCents = toCents(values.amount);
    if (!amountCents) return setError(t.common.invalid);
    setError(null);
    record.mutate(
      { unitId, date: values.date, amountCents, ...(values.reference.trim() ? { reference: values.reference.trim() } : {}) },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={t.statement.date}>
          <input type="date" value={values.date} onChange={set('date')} />
        </Field>
        <Field label={t.statement.amount} error={error ?? undefined}>
          <input value={values.amount} onChange={set('amount')} inputMode="decimal" placeholder="425,00" autoFocus />
        </Field>
        <Field label={t.statement.reference} hint={t.common.optional}>
          <input value={values.reference} onChange={set('reference')} placeholder="Chèque 1043" />
        </Field>
      </div>
      {record.isError && <Alert>{errorMessage(record.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={record.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
